import store from "@ui/stores";
import {getCurrentPosition, posToStr} from "@ui/common/utils";
import {round} from "lodash-es";

interface IWatcher {
  isEnabled() : boolean
  start(on_success: PositionCallback, on_error: PositionErrorCallback): void
  stop(): void
}

const defaultOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
}

// стандартный наблюдатель через watchPosition
class DefaultWatcher implements IWatcher {
  #options: PositionOptions
  #watcherId?: number

  constructor(options: PositionOptions = defaultOptions){
    this.#options = options
    this.#watcherId = undefined
  }

  isEnabled(): boolean{
    return !!this.#watcherId
  }

  start(on_success: PositionCallback, on_error: PositionErrorCallback): void {
    const { debug } = store
    const success_cb = (pos: GeolocationPosition) => {
      debug.info(`DefaultWatcher: ${posToStr(pos)}`)
      on_success(pos)
    }

    const error_cb = (error: GeolocationPositionError) => {
      debug.error(`DefaultWatcherError: ${error.code}, ${error.message}`)
      on_error(error)
    }

    this.#watcherId = navigator.geolocation.watchPosition(
      success_cb, error_cb, this.#options
    );
  }

  stop(): void {
    if (this.#watcherId)
      navigator.geolocation.clearWatch(this.#watcherId);
    this.#watcherId = undefined
  }
}

// наблюдатель через getCurrentPosition
class CurrentPositionWatcher implements IWatcher {
  #options: PositionOptions
  #enabled: boolean

  constructor(options: PositionOptions = defaultOptions){
    this.#options = options
    this.#enabled = false
  }

  isEnabled(): boolean{
    return this.#enabled
  }

  start(on_success: PositionCallback, on_error: PositionErrorCallback): void {
    const { debug } = store
    this.#enabled = true

    const getPosition = () => {
      if (this.#enabled)
        navigator.geolocation.getCurrentPosition(
          success_cb, error_cb, this.#options
        )
    }

    const success_cb = (pos: GeolocationPosition) => {
      debug.info(`CurrentPositionWatcher: ${posToStr(pos)}`);
      on_success(pos)
      getPosition()
    }

    const error_cb = (error: GeolocationPositionError) => {
      debug.error(`CurrentPositionWatcherError: ${error.code}, ${error.message}`);
      on_error(error)
      getPosition()
    }

    getPosition()
  }

  stop(): void {
    this.#enabled = false
  }
}

// както там сам генерирует координаты
class EmulatorWatcher implements IWatcher {
  #interval: number
  #intervalId?: number

  constructor(interval: number = 1000){
    this.#interval = interval
    this.#intervalId = undefined
  }

  isEnabled(): boolean {
    return !!this.#intervalId
  }

  start(on_success: PositionCallback, on_error: PositionErrorCallback): void {
		let prevPosition: GeolocationPosition | undefined = undefined
    let position: GeolocationPosition | undefined = undefined
		let i = 0, j = 0
    this.#intervalId = setInterval(async () => {
			i++
			let delta =  5000
	    if (i % 10 === 0 || j > 0) {
				delta = 100
        // что бы много неправильных точек было
        // if (j === 0) Math.random() > 0.5 && j++
        // else j++
        // if (j === 12) j = 0
	    }
			try {
				if (!position) {
						position = await getCurrentPosition();
				} else {
					if (i % 10 === 1 && prevPosition) {
						position = {...prevPosition}
					} else {
						prevPosition = {...position}
					}
					position = {
	          coords: {
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              latitude: round(parseFloat(String(position.coords.latitude)) - (Math.random() / delta), 7),
              longitude: round(parseFloat(String(position.coords.longitude)) - (Math.random() / delta), 7),
              speed: Math.random() * 10,
	          },
	          timestamp: Date.now(),
	        }
				}
				on_success({
          coords: {
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            speed: position.coords.speed,
          },
          timestamp: position.timestamp,
        });
			} catch (e) {
				on_error && on_error(e as GeolocationPositionError);
			}
		}, this.#interval) as unknown as number
    
  }

  stop(): void {
    if (this.#intervalId)
      clearInterval(this.#intervalId);
    this.#intervalId = undefined
  }
}

export interface IMovementService {
	readonly emulate: boolean
  readonly isAvailable: boolean 
  start(on_success: PositionCallback, on_error: PositionErrorCallback): void
  stop(): void
}

export class MovementService implements IMovementService {
  readonly emulate: boolean = process.env.EMULATE_GEO_MOVING === "true"
  readonly isAvailable: boolean = this.emulate || !!navigator.geolocation
  #watcher: IWatcher

  constructor() {
		this.#watcher = this.emulate ? new EmulatorWatcher() : new DefaultWatcher({enableHighAccuracy: true})
  }

  start(on_success: PositionCallback, on_error: PositionErrorCallback): void {
    if (!this.#watcher.isEnabled()) {
			this.#watcher.start(on_success, on_error)
		}
  }

  stop() {
    this.#watcher.stop()
  }

}

export default new MovementService() as IMovementService
